const STORAGE_KEY = 'websites.ownerLabelCache';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_ENTRIES = 500;

interface OwnerLabelEntry {
  email: string;
  at: number;
}

type OwnerLabelStore = { [userId: string]: OwnerLabelEntry };

function readStore(): OwnerLabelStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }

    return parsed as OwnerLabelStore;
  } catch (err) {
    return {};
  }
}

function writeStore(store: OwnerLabelStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (err) {
    // Ignore quota / private-mode failures.
  }
}

function isFresh(entry: OwnerLabelEntry, now: number = Date.now()): boolean {
  return !!entry
    && typeof entry.email === 'string'
    && !!entry.email
    && typeof entry.at === 'number'
    && (now - entry.at) < TTL_MS;
}

function pruneStore(store: OwnerLabelStore, now: number = Date.now()): OwnerLabelStore {
  const next: OwnerLabelStore = {};

  Object.keys(store).forEach((id) => {
    const entry = store[id];
    if (isFresh(entry, now)) {
      next[id] = entry;
    }
  });

  const ids = Object.keys(next);
  if (ids.length <= MAX_ENTRIES) {
    return next;
  }

  ids
    .sort((a, b) => next[a].at - next[b].at)
    .slice(0, ids.length - MAX_ENTRIES)
    .forEach((id) => {
      delete next[id];
    });

  return next;
}

export function getOwnerLabel(userId: string): string | null {
  if (!userId) {
    return null;
  }

  const store = readStore();
  const entry = store[userId];
  if (!isFresh(entry)) {
    return null;
  }

  return entry.email;
}

export function setOwnerLabel(userId: string, email: string): void {
  if (!userId || !email) {
    return;
  }

  const store = pruneStore(readStore());
  store[userId] = {
    email,
    at: Date.now(),
  };

  writeStore(pruneStore(store));
}

export function primeOwnerLabels(ids: string[]): { [id: string]: string } {
  const result: { [id: string]: string } = {};
  if (!ids || !ids.length) {
    return result;
  }

  const store = readStore();
  const now = Date.now();
  let dirty = false;

  ids.forEach((id) => {
    if (!id) {
      return;
    }

    const entry = store[id];
    if (!entry) {
      return;
    }

    if (!isFresh(entry, now)) {
      delete store[id];
      dirty = true;
      return;
    }

    result[id] = entry.email;
  });

  if (dirty) {
    writeStore(pruneStore(store, now));
  }

  return result;
}
